/**
 * CustomPageFields — BỘ Ô NHẬP cấp cho script trang tuỳ biến (custompage), dùng CHUNG component với form DataListManager
 * (BaseFieldControl): nhập số có phân cách hàng nghìn, chọn ngày / giờ / tháng có lịch (định dạng Việt Nam), soạn thảo văn bản
 * (HTML), soạn mã. Script nhận qua props (xem customPageSandbox.DO_NGHE_O_NHAP) và docs/custompage-guide.md mục "Ô nhập liệu".
 *
 * Nguyên tắc chung (để script dùng như TextField của MUI):
 *   - `value` + `onChange(giaTriMoi)` — onChange nhận THẲNG giá trị (không phải event).
 *   - `label`, `helperText`, `error`, `required`, `readOnly`, `disabled`, `sx`, `fullWidth`, `size` (mặc định 'small').
 *   - Ô số / văn bản gõ mượt (giữ giá trị tạm trong ô, báo lên sau ~0,4 giây hoặc khi rời ô) — bấm "Lưu" ngay sau khi gõ vẫn
 *     có số mới (rời ô báo ngay).
 */
import React, { useEffect, useState } from 'react';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import moment from 'moment';
import OptimizedTextField from './OptimizedTextField';
import ManagedDropdown from './ManagedDropdown';
import HtmlEditor from './HtmlEditor2';
import CodeEditorGoc from './CodeEditor';
import JsonObjectControl from './JsonObjectControl';
import BaseFieldControl from './BaseFieldControl';

const nenO = (readOnly) => ({ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: readOnly ? 'action.hover' : 'transparent' } });

// ───────────────────────────────────────── SỐ ─────────────────────────────────────────
/**
 * Ô SỐ: hiện 1.234.567,5 (dấu chấm hàng nghìn, phẩy thập phân), bàn phím số trên điện thoại.
 * onChange nhận Number, hoặc '' khi xoá trắng (để script phân biệt "chưa nhập" với 0).
 * @param decimals      số chữ số thập phân tối đa (mặc định 0 = số nguyên; null = không giới hạn)
 * @param min / max     min ≥ 0 thì không gõ được dấu trừ; max chặn không cho gõ quá
 * @param suffix/prefix vd suffix=' đ', suffix=' %'
 * @param thousandSeparator  false = không phân cách (mã số, năm…)
 */
export function NumberField({ value, onChange, decimals = 0, min, max, suffix, prefix, thousandSeparator = true, allowNegative,
  readOnly, InputProps, inputProps, sx, size = 'small', debounceTime = 400, ...rest }) {
  const amDuoc = allowNegative != null ? !!allowNegative : !(min != null && Number(min) >= 0);
  const sangSo = (v) => {
    if (v === '' || v == null || v === '-') return '';
    const n = Number(v);
    return Number.isFinite(n) ? n : '';
  };
  return (
    <OptimizedTextField
      isNumeric
      debounceTime={debounceTime}
      numericProps={{
        thousandSeparator: thousandSeparator ? '.' : undefined,
        decimalSeparator: ',',
        decimalScale: decimals == null ? undefined : decimals,
        allowNegative: amDuoc,
        suffix, prefix,
        isAllowed: max != null ? ({ floatValue }) => floatValue == null || floatValue <= Number(max) : undefined,
        inputProps: { inputMode: decimals ? 'decimal' : 'numeric', ...inputProps },
      }}
      value={value == null ? '' : value}
      onChange={(v) => { if (onChange) onChange(sangSo(v)); }}
      InputProps={{ readOnly, ...InputProps }}
      size={size}
      sx={{ ...nenO(readOnly), ...sx }}
      {...rest}
    />
  );
}

// ───────────────────────────────────── NGÀY / GIỜ / THÁNG ─────────────────────────────────────
const sangMoment = (v, dinhDang) => {
  if (v == null || v === '') return null;
  if (moment.isMoment(v)) return v;
  const m = typeof v === 'string' && dinhDang ? moment(v, dinhDang, true) : moment(v);
  return m.isValid() ? m : (typeof v === 'string' && dinhDang ? (moment(v).isValid() ? moment(v) : null) : null);
};
const khoaGiaTri = (v) => (v instanceof Date ? 'd' + v.getTime() : (moment.isMoment(v) ? 'm' + v.valueOf() : (v == null ? '' : String(v))));

/**
 * Bộ chọn chung: giữ giá trị ĐANG GÕ trong ô (ngày gõ dở là "không hợp lệ" — không báo lên cha, cha giữ giá trị hợp lệ cuối),
 * chọn / gõ xong hợp lệ thì báo lên: `valueFormat` có -> CHUỖI theo định dạng đó (vd 'YYYY-MM-DD'), không có -> Date. Xoá trắng -> null
 * (có valueFormat thì '').
 */
function BoChon({ loai, hien, vfMacDinh, value, onChange, valueFormat, label, helperText, error, required, readOnly, disabled,
  fullWidth = true, size = 'small', sx, minDate, maxDate, views, openTo, clearable = true, slotProps, ...rest }) {
  const vf = valueFormat === undefined ? vfMacDinh : valueFormat;
  const [m, setM] = useState(() => sangMoment(value, vf));
  const khoa = khoaGiaTri(value);
  useEffect(() => { setM(sangMoment(value, vf)); }, [khoa, vf]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ManagedDropdown
      type={loai}
      label={label}
      value={m}
      readOnly={readOnly}
      disabled={disabled}
      format={hien}
      views={views}
      openTo={openTo}
      ampm={loai === 'datepicker' ? undefined : false}
      minDate={minDate != null ? sangMoment(minDate, vf) || undefined : undefined}
      maxDate={maxDate != null ? sangMoment(maxDate, vf) || undefined : undefined}
      onChange={(moi) => {
        setM(moi);
        if (!onChange) return;
        if (!moi) { onChange(vf ? '' : null); return; }
        if (moment.isMoment(moi) && !moi.isValid()) return;
        const mm = moment(moi);
        onChange(vf ? mm.format(vf) : mm.toDate());
      }}
      slotProps={{
        ...slotProps,
        field: { clearable: clearable && !readOnly, ...(slotProps && slotProps.field) },
        textField: { size, fullWidth, required, error: !!error, helperText, sx: { ...nenO(readOnly), ...sx }, ...(slotProps && slotProps.textField) },
      }}
      {...rest}
    />
  );
}

/** Ô NGÀY dd/mm/yyyy. Mặc định onChange(Date); valueFormat='YYYY-MM-DD' -> onChange('2026-11-05') và value nhận chuỗi đó. */
export function DateField(props) { return <BoChon loai='datepicker' hien='DD/MM/YYYY' {...props} />; }
/** Ô NGÀY GIỜ dd/mm/yyyy HH:mm (24 giờ). Mặc định onChange(Date). */
export function DateTimeField(props) { return <BoChon loai='datetimepicker' hien='DD/MM/YYYY HH:mm' {...props} />; }
/** Ô GIỜ HH:mm (24 giờ). Mặc định onChange(Date); valueFormat='HH:mm' -> onChange('08:30') và value nhận '08:30'. */
export function TimeField(props) { return <BoChon loai='timepicker' hien='HH:mm' {...props} />; }
/** Ô THÁNG mm/yyyy. Mặc định value / onChange là CHUỖI 'YYYY-MM' (kỳ báo cáo, bảng kê); valueFormat={null} -> Date ngày 1. */
export function MonthField(props) {
  return <BoChon loai='datepicker' hien='MM/YYYY' views={['year', 'month']} openTo='month' vfMacDinh='YYYY-MM' {...props} />;
}

// ───────────────────────────────────── VĂN BẢN / MÃ ─────────────────────────────────────
const NhanO = ({ label, required, error }) => (label ? (
  <Typography variant='caption' color={error ? 'error.main' : 'text.secondary'} sx={{ fontWeight: 'bold', mb: 0.5, ml: 1, display: 'block' }}>
    {label} {required ? <span style={{ color: 'red' }}>*</span> : null}
  </Typography>
) : null);

/**
 * SOẠN THẢO VĂN BẢN (HTML — trình soạn của hệ thống, chèn ảnh / bảng / liên kết). onChange(chuoiHtml).
 * Chỉ dùng cho nội dung HIỂN THỊ DẠNG HTML (email, trang web) — tin Zalo / SMS / Messenger là chữ thường, đừng dùng ô này.
 * @param config  cấu hình Jodit THAY HẲN bộ mặc định (thanh công cụ đầy đủ, cao 200px) — thường để trống
 */
export function RichTextEditor({ value, onChange, label, helperText, error, required, readOnly, disabled, placeholder, config, sx }) {
  return (
    <FormControl fullWidth error={!!error} disabled={disabled} sx={sx}>
      <NhanO label={label} required={required} error={error} />
      <HtmlEditor data={value || ''} onChange={(e, html) => { if (onChange) onChange(html); }} readOnly={readOnly || disabled}
        placeholder={placeholder} config={config} helperText={typeof helperText === 'string' ? helperText : undefined} />
      {helperText && typeof helperText !== 'string' ? <FormHelperText sx={{ mx: 1.5 }}>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

/** SOẠN MÃ (tô màu cú pháp JavaScript). onChange(chuoi). Giá trị là object -> hiện dạng JSON. */
export function CodeEditor({ value, onChange, label, helperText, error, required, readOnly, disabled, placeholder, sx }) {
  const chuoi = value !== null && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value == null ? '' : String(value));
  return (
    <FormControl fullWidth error={!!error} disabled={disabled} sx={sx}>
      <NhanO label={label} required={required} error={error} />
      <CodeEditorGoc data={chuoi} onChange={(e, ma) => { if (onChange) onChange(ma); }} disabled={!!(readOnly || disabled)} placeholder={placeholder}
        helperText={typeof helperText === 'string' ? helperText : undefined} />
    </FormControl>
  );
}

/** SOẠN OBJECT JSON (từng khoá / giá trị) như ô kiểu Object của form. onChange(object). */
export function JsonEditor({ value, onChange, label, helperText, error, required, readOnly, sx }) {
  return (
    <FormControl fullWidth error={!!error} sx={sx}>
      <NhanO label={label} required={required} error={error} />
      <JsonObjectControl data={value} onChange={(e, o) => { if (onChange) onChange(o); }} isReadOnly={readOnly} helperText={helperText} />
    </FormControl>
  );
}

// ───────────────────────────────────── Ô THEO KHAI BÁO FIELD ─────────────────────────────────────
/**
 * Ô dựng y hệt form DataListManager từ một khai báo field: `field.type` = String | Number | Date | DateTime | Time | Boolean | Radio |
 * Select | Autocomplete | Slider | NumberRange | DateFilter | RichText | Code | Object | Mixed | ObjectArray…, kèm label, required,
 * options, api / apiMapping (Select/Autocomplete), multiline, maxLength, helpText…
 * Dùng khi trang có cả một danh sách ô khai bằng dữ liệu. Ngày giờ ở đây trả Date (như form) — cần chuỗi thì dùng DateField.
 */
export function FieldControl({ field, value, onChange, error, readOnly, formData, ...rest }) {
  return (
    <Box sx={{ width: '100%' }}>
      <BaseFieldControl field={field || {}} value={value} onChange={onChange} error={error} isReadOnly={!!readOnly} formData={formData || {}} {...rest} />
    </Box>
  );
}

/** Gom để DynamicCustomPage truyền một lần (khớp customPageSandbox.DO_NGHE_O_NHAP). */
export const BO_O_NHAP = { FieldControl, NumberField, DateField, DateTimeField, TimeField, MonthField, RichTextEditor, CodeEditor, JsonEditor };

export default BO_O_NHAP;
